import { useMemo, useState } from 'react'
import { useStore } from '../store/store'
import type { BetResult, EmotionalState, JournalEntry, PositionKind } from '../domain/types'
import { TOMORROW_CHECKLIST, checklistVerdict, journalStats, redTeamIdea } from '../lib/behavior'
import { fmtCurrency, fmtPct } from '../lib/format'
import { Banner, Button, Card, NumberField, PageHead, Pill, SelectField, Stat, TextArea, TextField, Toggle } from '../components/ui'
import { BarChart } from '../components/charts'

const EMOTIONS: EmotionalState[] = ['calm', 'confident', 'fomo', 'revenge', 'bored', 'tilted', 'fearful']

const emptyEntry = (): Omit<JournalEntry, 'id' | 'createdAt'> => ({
  kind: 'sportsbook',
  title: '',
  thesis: '',
  probabilityEstimate: 0.55,
  marketPriceOrOdds: '',
  expectedValue: 0,
  stake: 0,
  reasonEnter: '',
  reasonSkip: '',
  emotionalState: 'calm',
  result: 'pending',
  lessons: '',
  followedRules: true,
  tags: [],
})

export function Journal() {
  const entries = useStore((s) => s.journal)
  const add = useStore((s) => s.addJournalEntry)
  const update = useStore((s) => s.updateJournalEntry)
  const remove = useStore((s) => s.removeJournalEntry)

  const [draft, setDraft] = useState(emptyEntry())
  const [checks, setChecks] = useState<Record<string, boolean>>({})
  const stats = useMemo(() => journalStats(entries), [entries])
  const verdict = checklistVerdict(checks)

  const set = <K extends keyof JournalEntry>(k: K, v: JournalEntry[K]) => setDraft((d) => ({ ...d, [k]: v }))

  const save = () => {
    if (!draft.title) return
    add(draft)
    setDraft(emptyEntry())
    setChecks({})
  }

  return (
    <div className="stack">
      <PageHead title="Trading & Betting Journal">
        Every idea — taken or skipped — gets a thesis, a probability, a price and an emotional read. The journal is where edges are actually found.
      </PageHead>

      <div className="grid cols-4">
        <Stat label="Entries" value={String(stats.count)} sub={`${stats.skips} logged skips`} />
        <Stat label="Win rate" value={fmtPct(stats.winRate, 0)} sub={`${stats.wins}W / ${stats.losses}L`} />
        <Stat label="Rule adherence" value={fmtPct(stats.ruleFollowRate, 0)} tone={stats.ruleFollowRate > 0.8 ? 'pos' : 'neg'} sub="followed your own plan" />
        <Stat label="Tilt entries" value={String(stats.tiltCount)} tone={stats.tiltCount > 0 ? 'neg' : 'pos'} sub="fomo / revenge / tilted" />
      </div>

      <div className="grid cols-2">
        <Card title="New entry">
          <div className="form-grid">
            <TextField label="Title" value={draft.title} onChange={(v) => set('title', v)} placeholder="e.g. NOVA breakout long" />
            <SelectField label="Type" value={draft.kind} onChange={(v: PositionKind) => set('kind', v)} options={[{ value: 'stock', label: 'Stock' }, { value: 'sportsbook', label: 'Sportsbook' }, { value: 'prediction', label: 'Prediction' }]} />
            <NumberField label="Probability estimate" value={draft.probabilityEstimate} onChange={(v) => set('probabilityEstimate', v)} step={0.01} min={0} max={1} />
            <TextField label="Market price / odds" value={draft.marketPriceOrOdds} onChange={(v) => set('marketPriceOrOdds', v)} placeholder="2.10 / -110 / 47¢" />
            <NumberField label="Expected value (per $1)" value={draft.expectedValue} onChange={(v) => set('expectedValue', v)} step={0.01} />
            <NumberField label="Stake (0 = skip)" value={draft.stake} onChange={(v) => set('stake', v)} step={10} />
            <SelectField label="Emotional state" value={draft.emotionalState} onChange={(v: EmotionalState) => set('emotionalState', v)} options={EMOTIONS.map((e) => ({ value: e, label: e }))} />
            <SelectField label="Result" value={draft.result} onChange={(v: BetResult) => set('result', v)} options={[{ value: 'pending', label: 'pending' }, { value: 'win', label: 'win' }, { value: 'loss', label: 'loss' }, { value: 'push', label: 'push' }]} />
          </div>
          <TextArea label="Thesis" value={draft.thesis} onChange={(v) => set('thesis', v)} placeholder="Why does this have an edge?" />
          <TextArea label="Reason to enter" value={draft.reasonEnter} onChange={(v) => set('reasonEnter', v)} rows={2} />
          <TextArea label="Reason to skip (if skipping)" value={draft.reasonSkip} onChange={(v) => set('reasonSkip', v)} rows={2} />
          <TextArea label="Lessons learned" value={draft.lessons} onChange={(v) => set('lessons', v)} rows={2} />
          <Toggle label="I followed my own rules on this one" checked={draft.followedRules} onChange={(v) => set('followedRules', v)} />
          <Button variant="primary" onClick={save} disabled={!draft.title}>Save entry</Button>
        </Card>

        <div className="stack">
          <Card title={'\u2600 Would I still take this tomorrow?'} subtitle="If you can't check every box, sleep on it">
            {TOMORROW_CHECKLIST.map((item) => (
              <Toggle key={item.id} label={item.question} checked={!!checks[item.id]} onChange={(v) => setChecks((c) => ({ ...c, [item.id]: v }))} />
            ))}
            <Banner tone={verdict.ready ? 'info' : 'warn'}>
              {verdict.ready ? 'All clear — this idea survives a cool head.' : `${verdict.failing.length} unchecked. That hesitation is information.`}
            </Banner>
          </Card>

          <Card title={'\uD83D\uDD25 Red Team this idea'} subtitle="Auto-generated counterarguments from your draft">
            <ul style={{ paddingLeft: 18, margin: 0 }}>
              {redTeamIdea({ modelProb: draft.probabilityEstimate, marketProb: Math.max(0.01, draft.probabilityEstimate - draft.expectedValue), edge: draft.expectedValue, dataQuality: 0.6, confidence: 0.6, liquidity: 0.6 }).slice(0, 4).map((a, i) => (
                <li key={i} style={{ marginBottom: 5 }}>{a}</li>
              ))}
            </ul>
          </Card>
        </div>
      </div>

      <Card title="Discipline: EV of every logged idea" subtitle="Green = positive expected value when you entered">
        {entries.length === 0 ? <div className="empty">No entries yet.</div> : <BarChart values={entries.slice().reverse().map((e) => e.expectedValue)} color="auto" height={120} />}
      </Card>

      <Card title="History">
        {entries.length === 0 ? (
          <div className="empty">Your journal is empty.</div>
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead><tr><th>Date</th><th>Title</th><th>Type</th><th>Prob</th><th>Stake</th><th>State</th><th>Rules</th><th>Result</th><th></th></tr></thead>
              <tbody>
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="mono faint">{e.createdAt.slice(0, 10)}</td>
                    <td>{e.title}{e.stake === 0 && <> <Pill tone="amber">skip</Pill></>}</td>
                    <td><span className="tag">{e.kind}</span></td>
                    <td className="mono">{fmtPct(e.probabilityEstimate, 0)}</td>
                    <td className="mono">{e.stake > 0 ? fmtCurrency(e.stake) : '—'}</td>
                    <td><Pill tone={['fomo', 'revenge', 'tilted'].includes(e.emotionalState) ? 'red' : 'gray'}>{e.emotionalState}</Pill></td>
                    <td>{e.followedRules ? <span className="pos">{'\u2713'}</span> : <span className="neg">{'\u2717'}</span>}</td>
                    <td>
                      <select value={e.result} onChange={(ev) => update(e.id, { result: ev.target.value as BetResult })} style={{ maxWidth: 110 }}>
                        <option value="pending">pending</option>
                        <option value="win">win</option>
                        <option value="loss">loss</option>
                        <option value="push">push</option>
                      </select>
                    </td>
                    <td><button className="btn sm danger" onClick={() => remove(e.id)}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
